/**
 * DSButton Component
 *
 * Button wrapper using design system button classes.
 * Supports multiple variants and sizes.
 *
 * Usage:
 * <DSButton variant="primary" size="md" onClick={handleClick}>
 *   Submit
 * </DSButton>
 *
 * Variants:
 * - primary (navy-600) - Main actions
 * - secondary (teal-600) - Secondary actions
 * - accent (lime-500) - Accent actions
 * - outline - Outlined style
 * - ghost - Minimal style
 * - danger (red) - Destructive actions
 *
 * Sizes:
 * - xs, sm, md (default), lg, xl
 */

import React from 'react';
import { Button } from '@mui/material';
import PropTypes from 'prop-types';
import { DSSpinner } from './DSSpinner';

export const DSButton = React.memo(({
  variant = 'primary',
  size = 'md',
  children,
  disabled,
  loading,
  fullWidth = false,
  className = '',
  type = 'button',
  ...otherProps
}) => {
  // Map variant names to design system button classes
  const variantMap = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    accent: 'btn-accent',
    outline: 'btn-outline',
    ghost: 'btn-ghost',
    danger: 'btn-danger',
  };

  // Map size names to design system button classes
  const sizeMap = {
    xs: 'btn-xs',
    sm: 'btn-sm',
    md: 'btn-md',
    lg: 'btn-lg',
    xl: 'btn-xl',
  };

  const buttonClassName = `btn ${variantMap[variant] || variantMap.primary} ${sizeMap[size] || sizeMap.md} ${className}`;

  return (
    <Button
      type={type}
      className={buttonClassName}
      disabled={disabled || loading}
      sx={{
        // Override MUI defaults with design system classes
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        letterSpacing: '0.3px',
        textTransform: 'none',
        width: fullWidth ? '100%' : 'auto',
        '&.btn': {
          display: fullWidth ? 'flex' : 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          border: 'none',
          cursor: 'pointer',
          transition: 'all var(--duration-normal) var(--ease-out)',
          borderRadius: 'var(--radius-md)',
          outline: 'none',
          position: 'relative',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          minHeight: 'var(--touch-target-min)',
          '&:focus-visible': {
            boxShadow: '0 0 0 3px rgba(0,128,128,0.35)',
          },
          '&:disabled': {
            opacity: 0.45,
            cursor: 'not-allowed',
          },
        },
      }}
      {...otherProps}
    >
      {loading ? <DSSpinner variant="button" size="xs" /> : children}
    </Button>
  );
});

DSButton.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'accent', 'outline', 'ghost', 'danger']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
};

export default DSButton;
